import { useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, useHistory } from 'quartz-routing/react-router';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { View } from 'foundations-components/components/View';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { VideoAssetTypename } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import LessonInfoTabs from './LessonInfoTabs';
import StoredContentContainer from '../../containers/StoredContentContainer';
import { LessonContentWithInteractiveEducation } from './LessonContentWithInteractiveEducation';
import { PRE_ASSESSMENT_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS, TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT } from '../../constants/Navigation';
import { SCREENS } from '../../constants/Tracking';
import { HasGate } from '../../features/accounts/components/HasGate';
import { useLessonQuizMe, useLessonQuizMeVideoRequestParams } from '../../features/interactiveEducation/hooks/useLessonQuizMe';
import { LessonRecommendationsPage } from '../../features/lessons/components/LessonRecommendationsPage';
import { LessonPreQuizAlert } from '../../features/preAssessmentQuiz/components/LessonPreQuizAlert';
import { isVideoAsset } from '../../features/tracks/tracksUtils';
import { LessonPreQuizContainer } from '../../features/preAssessmentQuiz/components/LessonPreQuizContainer';
import { NextPlaylistContentOverview, PlaylistComplete, PlaylistIncompleteItems } from '../../features/playlistContent';
import { OptionalToolRecommendationSection } from '../../features/toolRecommendations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LessonPage = ({
  content,
  contentFragment,
  contentProgress,
  lesson,
  lessonProgress,
  onAdvanceContent,
  onSkipPreAssessment,
  onStartLessonWithPreAssessmentFocus,
  onResourceClick
}) => {
  var _lesson$useCases;
  const {
    push
  } = useHistory();
  const {
    isNotLaptop
  } = useMediaQuery();
  const {
    allVideosHaveCaptions,
    loading: captionsLoading
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson.content
  });
  const {
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    regenerate: regenerateQuiz,
    regenerateSingleVideoQuiz
  } = useLessonQuizMe({
    lesson
  });
  const lessonQuizProps = useMemo(() => ({
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    regenerateQuiz,
    regenerateSingleVideoQuiz
  }), [captionsExistMap, generateQuizLoading, generateQuizError, regenerateQuiz, regenerateSingleVideoQuiz]);
  const videoCount = lesson.content.filter(isVideoAsset).length;
  const preAssessmentAvailable = isNotLaptop && !captionsLoading && allVideosHaveCaptions && videoCount > 1;
  const handlePreAssessmentClick = () => push(`/lessons/${lesson.id}/${PRE_ASSESSMENT_URL_FRAGMENT}`);
  const useCases = (_lesson$useCases = lesson.useCases) === null || _lesson$useCases === void 0 ? void 0 : _lesson$useCases.map(readUnhandledEnumCase);
  const sectionViewProps = {
    backgroundColor: 'fill-surface-default',
    borderColor: 'border-core-subtle',
    borderRadius: '300',
    borderStyle: 'solid',
    borderWidth: '100',
    overflow: 'hidden'
  };
  switch (contentFragment) {
    case PRE_ASSESSMENT_URL_FRAGMENT:
      return /*#__PURE__*/_jsxs(HasGate, {
        fallback: /*#__PURE__*/_jsx(Redirect, {
          to: `/lessons/${lesson.id}`
        }),
        gate: "Academy:LessonPreAssessment",
        children: [/*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
          children: /*#__PURE__*/_jsx(LessonPreQuizContainer, {
            lesson: lesson,
            onSkip: onSkipPreAssessment,
            onStartLessonWithPreAssessmentFocus: onStartLessonWithPreAssessmentFocus,
            screen: SCREENS.LESSON
          })
        })), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsx(LessonInfoTabs, {
            content: content,
            lesson: lesson,
            onResourceClick: onResourceClick
          })
        }))]
      });
    case TRACK_RECOMMENDATIONS_URL_FRAGMENT:
      return /*#__PURE__*/_jsx(LessonRecommendationsPage, {
        id: lesson.id,
        name: lesson.name
      });
    case TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT:
      return /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        children: /*#__PURE__*/_jsx(NextPlaylistContentOverview, {
          lessonId: lesson === null || lesson === void 0 ? void 0 : lesson.id
        })
      }));
    case TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT:
      return /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        children: /*#__PURE__*/_jsx(PlaylistIncompleteItems, {})
      }));
    case TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT:
      return /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        children: /*#__PURE__*/_jsx(PlaylistComplete, {})
      }));
    case SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
          gate: "Academy:LessonPreAssessment",
          children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
            lesson: lesson,
            onClick: handlePreAssessmentClick,
            screen: SCREENS.LESSON
          })
        }), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
          children: /*#__PURE__*/_jsx(OptionalToolRecommendationSection, {
            contentName: lesson.name,
            lessonId: lesson.id,
            useCases: useCases
          })
        })), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsx(LessonInfoTabs, {
            content: content,
            lesson: lesson,
            onResourceClick: onResourceClick
          })
        }))]
      });
    default:
      // Render video content with interactive education features
      if ((content === null || content === void 0 ? void 0 : content.__typename) === VideoAssetTypename && content !== null && content !== void 0 && content.id) {
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
            gate: "Academy:LessonPreAssessment",
            children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
              lesson: lesson,
              onClick: handlePreAssessmentClick,
              screen: SCREENS.LESSON
            })
          }), /*#__PURE__*/_jsx(LessonContentWithInteractiveEducation, {
            lesson: lesson,
            lessonContentId: content.id,
            lessonQuizProps: lessonQuizProps,
            onAdvanceContent: onAdvanceContent,
            onResourceClick: onResourceClick,
            videoContent: content
          }), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
            marginBlockStart: "600",
            children: /*#__PURE__*/_jsx(LessonInfoTabs, {
              content: content,
              lesson: lesson,
              onResourceClick: onResourceClick
            })
          }))]
        });
      }

      // Render non-video content
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
          gate: "Academy:LessonPreAssessment",
          children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
            lesson: lesson,
            onClick: handlePreAssessmentClick,
            screen: SCREENS.LESSON
          })
        }), content ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
            children: /*#__PURE__*/_jsx(StoredContentContainer, {
              content: content,
              contentProgress: contentProgress,
              lesson: lesson,
              lessonProgress: lessonProgress,
              onFinish: onAdvanceContent
            })
          })), lesson && /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
            marginBlockStart: "600",
            children: /*#__PURE__*/_jsx(LessonInfoTabs, {
              content: content,
              lesson: lesson,
              onResourceClick: onResourceClick
            })
          }))]
        }) : /*#__PURE__*/_jsx(NavMarker, {
          name: "LESSON_VIEW_CONTENT_MISSING_ERROR",
          children: /*#__PURE__*/_jsx(ACMissingContentError, {})
        })]
      });
  }
};
export default LessonPage;